import { Hash } from 'lucide-react'
import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { searchTypeLabels } from '@/api/endpoints'
import type { SearchType, TimelineKind } from '@/api/types'
import { PageHeader } from '@/app/layouts/PageHeader'
import { EmptyState, ErrorState } from '@/components/ui/EmptyState'
import { PostListSkeleton } from '@/components/ui/Skeleton'
import { PostList } from '@/features/posts'
import { UserListItem } from '@/features/profile'
import {
  SEARCH_TYPES,
  SearchBox,
  useSearchHashtags,
  useSearchPosts,
  useSearchUsers,
} from '@/features/search'
import { KindFilter } from '@/features/timeline'
import { useT } from '@/i18n'
import { cn } from '@/lib/cn'

export function SearchPage() {
  const t = useT()
  const [params, setParams] = useSearchParams()
  const q = params.get('q')?.trim() ?? ''
  const type = (params.get('type') as SearchType) ?? 'posts'
  const [kind, setKind] = useState<TimelineKind>('all')

  const setType = (next: SearchType) => {
    const updated = new URLSearchParams(params)
    updated.set('type', next)
    setParams(updated)
  }

  return (
    <>
      <PageHeader title={t('search.title')}>
        <div className="px-4 pb-3">
          <SearchBox initial={q} autoFocus={!q} />
        </div>
        {q && (
          <nav className="flex" aria-label={t('search.typeLabel')}>
            {SEARCH_TYPES.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setType(item)}
                aria-current={type === item ? 'page' : undefined}
                className={cn(
                  'flex-1 py-3 text-[15px] transition-colors hover:bg-surface-hover',
                  type === item ? 'font-bold' : 'text-ink-muted',
                )}
              >
                <span
                  className={cn(
                    'inline-block pb-2',
                    type === item && 'border-b-4 border-brand-ink',
                  )}
                >
                  {t(searchTypeLabels[item])}
                </span>
              </button>
            ))}
          </nav>
        )}
        {q && type === 'posts' && (
          <div className="px-4 pb-2">
            <KindFilter value={kind} onChange={setKind} />
          </div>
        )}
      </PageHeader>

      {!q && <EmptyState title={t('search.promptTitle')} description={t('search.promptBody')} />}

      {q && type === 'posts' && <PostResults q={q} kind={kind} />}
      {q && type === 'users' && <UserResults q={q} />}
      {q && type === 'hashtags' && <HashtagResults q={q} />}
    </>
  )
}

function PostResults({ q, kind }: { q: string; kind: TimelineKind }) {
  const t = useT()
  const query = useSearchPosts(q, kind)
  return <PostList query={query} emptyTitle={t('search.noPosts', { q })} />
}

function UserResults({ q }: { q: string }) {
  const t = useT()
  const { data, isPending, isError, error, refetch } = useSearchUsers(q)

  if (isPending) return <PostListSkeleton count={4} />
  if (isError) return <ErrorState message={error.message} onRetry={() => refetch()} />
  if (data.length === 0) return <EmptyState title={t('search.noUsers', { q })} />

  return (
    <>
      {data.map((user) => (
        <UserListItem key={user.id} user={user} />
      ))}
      {/* 用户搜索按匹配质量排序、只给一页，见 backend/app/services/search.py */}
      <p className="py-8 text-center text-[15px] text-ink-muted">{t('search.bestMatches')}</p>
    </>
  )
}

function HashtagResults({ q }: { q: string }) {
  const t = useT()
  const { data, isPending, isError, error, refetch } = useSearchHashtags(q)

  if (isPending) return <PostListSkeleton count={4} />
  if (isError) return <ErrorState message={error.message} onRetry={() => refetch()} />
  if (data.length === 0) return <EmptyState title={t('search.noTags', { q })} />

  return (
    <>
      {data.map((tag) => (
        <Link
          key={tag.name}
          to={`/hashtag/${encodeURIComponent(tag.name)}`}
          className="flex items-center gap-3 border-b border-line px-4 py-3 hover:bg-surface-hover"
        >
          <Hash className="size-5 shrink-0 text-ink-muted" />
          <span className="min-w-0">
            <span className="block truncate text-[15px] font-bold">#{tag.display}</span>
            <span className="block text-[13px] text-ink-muted">
              {t('profile.postCount', { count: tag.post_count })}
            </span>
          </span>
        </Link>
      ))}
    </>
  )
}
